import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  toggleLanguage: () => void;
  t: (key: string) => string;
  translateDescription: (text: string) => string;
  isEnglish: boolean;
  isThai: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// English translations
const en = {
  freelance: {
    title: 'Angel Freelance',
    intro: 'Join Angel Freelance. Fill in your details and upload two recent photos. Our team will review your application and it will be displayed within 24 hours.',
    formTitle: 'Register your details',
    displayName: 'Name to be displayed',
    firstName: 'First name',
    lastName: 'Last name',
    location: 'Location',
    locationPlaceholder: 'e.g. Sukhumvit, Bangkok',
    workTelephone: 'Work telephone number',
    workLineId: 'Work Line ID',
    workWhatsapp: 'Work WhatsApp',
    email: 'Email',
    emailHint: 'if available',
    age: 'Age',
    height: 'Height',
    heightPlaceholder: 'e.g. 168 cm',
    contactNote: 'Provide a Work Line ID and/or WhatsApp number — at least one is required.',
    optional: 'optional',
    photo1: 'Upload Photo 1',
    photo2: 'Upload Photo 2',
    photoHint: 'JPG or PNG, up to 8 MB',
    changePhoto: 'Change',
    submit: 'Submit Application',
    submitting: 'Submitting…',
    successTitle: 'Thank you!',
    successMsg: 'Your details have been submitted and are under review. We will be in touch soon.',
    submitAnother: 'Submit another application',
    errRequired: 'Please fill in all required fields.',
    errContact: 'Please provide a Work Line ID or WhatsApp number.',
    errPhotos: 'Please upload both photos.',
    errPhotoType: 'Photos must be JPG or PNG.',
    errPhotoSize: 'Each photo must be 8 MB or smaller.',
    errSubmit: 'Something went wrong. Please try again.',
    galleryTitle: 'Our Freelancers',
    galleryEmpty: 'No freelancers to show yet. Please check back soon.',
    profilesTitle: 'Angel Freelance Profiles',
    profilesSubtitle: 'Browse our available freelancers. Tap a photo to view it full size.',
    viewProfiles: 'View Profiles',
    registerCta: 'Submit Your Profile',
    close: 'Close',
    lineLabel: 'LINE',
    whatsappLabel: 'WhatsApp',
    yearsOld: 'yrs',
  },
  // Common
  common: {
    refresh: 'Refresh',
    logout: 'Logout',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    loading: 'Loading...',
    submit: 'Submit',
    back: 'Back',
    next: 'Next',
    done: 'Done',
    clear: 'Clear',
    clearFilters: 'Clear Filters',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    available: 'Available',
    unavailable: 'Unavailable',
    pending: 'Pending',
    assigned: 'Assigned',
    accepted: 'Accepted',
    completed: 'Completed',
    cancelled: 'Cancelled',
    unassigned: 'Unassigned',
    to: 'to',
    minutes: 'minutes',
    min: 'min',
    bookings: 'bookings',
    booking: 'booking',
  },
  
  // Home page
  home: {
    heroTitle: 'Angel Massage Bangkok | 24/7 Home & Hotel Service',
    heroSubtitle: "Relieve stress 24/7 with Sukhumvit's top visiting massage service. Experienced therapists at your home or hotel in Bangkok. Book via our website or LINE today!",
    tagline: 'Professional massage service in Bangkok • Available 24/7',
    staffPortal: 'Staff Portal',
    footer: '© 2024 Angel Massage. Available 24/7 in Bangkok area.',
    footerTravel: 'Travel included within 6km from Sukhumvit 20. Taxi charges apply beyond 6km — see rates below.',
    ourTherapists: 'Our Therapists',
    serviceRates: 'Service Rates',
    bookNow: 'Book Now',
    addFriendOnLine: 'Add Friend on LINE',
    freelanceButton: 'Click here for Angel Freelance',
    freelanceUnderConstruction: 'Under Construction',
    freelanceComingSoon: "We're building something special. Please check back soon.",
    backToHome: 'Back to Home',
    lineQuestionNote: 'Any questions? Message us on LINE and ask!',
  },

  // Customer reviews
  reviews: {
    title: 'What Our Customers Say',
    subtitle: 'Real reviews from clients across Bangkok',
    outOf5: 'out of 5',
    basedOn: 'Based on',
    reviewsWord: 'reviews',
    reviewWordSingular: 'review',
    verified: 'Verified customer',
    withTherapist: 'with',
  },

  // Therapist Gallery
  gallery: {
    title: 'Our Professional Therapists',
    subtitle: 'Experienced and certified massage therapists available 24/7',
    viewPhotos: 'View photos',
  },
  
  // Service Rates
  rates: {
    title: 'Services & Rates',
    subtitle: 'Premium massage services at competitive prices',
    travelNote: 'Travel is included for locations within 6km from our base at Sukhumvit 20. For locations beyond 6km, taxi surcharges apply as per the rate card below.',
  },
  
  // Booking form
  booking: {
    title: 'Book Your Massage',
    formDescription: 'Fill in your details and we\'ll confirm your booking',
    personalInfo: 'Personal Information',
    name: 'Your Name',
    namePlaceholder: 'Enter your name',
    contactNumber: 'Contact Number',
    contactPlaceholder: '+66...',
    preferredLanguage: 'Preferred Chat Language',
    location: 'Location',
    hotelName: 'Hotel / Address',
    hotelPlaceholder: 'Enter hotel name or home address',
    roomNumber: 'Room Number',
    roomPlaceholder: 'Room number',
    contactMethod: 'Preferred Contact Method',
    lineId: 'LINE ID (Optional)',
    lineIdPlaceholder: '@yourlineid',
    lineIdHelp: 'Enter your LINE ID starting with @',
    linePlaceholder: '@yourlineid',
    whatsapp: 'WhatsApp (Optional)',
    whatsappPlaceholder: '+66812345678',
    whatsappHelp: 'Include country code (e.g., +66 for Thailand)',
    contactRequired: 'Provide at least one contact method',
    serviceDetails: 'Service Details',
    serviceType: 'Service Type',
    selectService: 'Select a service',
    duration: 'Duration',
    selectDuration: 'Select duration',
    totalPrice: 'Total Price',
    preferredTherapist: 'Preferred Therapist (Optional)',
    anyTherapist: 'Any available therapist',
    preferredDateTime: 'Preferred Date & Time (Optional)',
    specialRequests: 'Special Requests (Optional)',
    specialPlaceholder: 'Any special requests or notes...',
    submitBooking: 'Submit Booking',
    submitting: 'Submitting...',
    bookingSuccess: 'Booking submitted successfully!',
    bookingSuccessDesc: 'We will contact you shortly to confirm.',
    price: 'Price',
  },
  
  // Login page
  login: {
    staffPortal: 'Staff Portal',
    therapist: 'Therapist',
    admin: 'Admin',
    therapistLogin: 'Therapist Login',
    adminLogin: 'Admin Login',
    enterOtp: 'Enter the OTP code sent to your LINE',
    enterPhone: 'Enter your phone number to receive an OTP via LINE',
    phoneNumber: 'Phone Number',
    phonePlaceholder: '0971344376',
    sendOtp: 'Send OTP via LINE',
    haveOtp: 'I have an OTP from admin',
    enterAdminOtp: 'Enter the OTP code provided by your admin.',
    or: 'or',
    sendingOtp: 'Sending OTP...',
    enterOtpCode: 'Enter OTP Code',
    verifyLogin: 'Verify & Login',
    verifying: 'Verifying...',
    differentPhone: 'Use different phone number',
    email: 'Email',
    emailPlaceholder: 'admin@angelmassage.com',
    password: 'Password',
    loginBtn: 'Login',
    loggingIn: 'Logging in...',
    backToBooking: '← Back to Booking Page',
    adminCredentials: 'Enter your admin credentials',
    enterNamePassword: 'Enter your name and password to login',
    yourName: 'Your Name',
    namePlaceholder: 'e.g. Miw',
    forgotPassword: 'Forgot password? / First time login',
    verifyIdentity: 'Verify your identity with your name and phone number',
    identityInfo: 'Enter your name and phone number registered with your admin to verify your identity.',
    verifyAndContinue: 'Verify & Continue',
    backToLogin: 'Back to Login',
    askAdminOtp: 'Ask your admin to generate an OTP for you from the Admin Portal (Therapists tab).',
    createPassword: 'Create a password for future logins',
    otpVerified: 'OTP verified! Create a password for future logins.',
    newPassword: 'New Password',
    minChars: 'At least 6 characters',
    confirmPassword: 'Confirm Password',
    confirmPlaceholder: 'Re-enter password',
    saving: 'Saving...',
    setPasswordLogin: 'Set Password & Login',
  },
  
  // Admin dashboard
  admin: {
    title: 'Admin Dashboard',
    welcome: 'Welcome',
    changePassword: 'Change Password',
    dashboard: 'Dashboard',
    bookings: 'Bookings',
    calendar: 'Calendar',
    therapists: 'Therapists',
    revenue: 'Revenue',
    customers: 'Customers',
    totalBookings: 'Total Bookings',
    inProgress: 'In Progress',
    therapistAvailability: 'Therapist Availability',
    quickActions: 'Quick Actions',
    viewBookings: 'View Bookings',
    calendarView: 'Calendar View',
    addTherapist: 'Add Therapist',
    assignTherapist: 'Assign Therapist',
    reassign: 'Reassign',
    markComplete: 'Mark Complete',
    selectDate: 'Select Date',
    bookingsFor: 'Bookings for',
    noBookingsDate: 'No bookings on this date',
    pinkDatesHaveBookings: 'Pink dates have bookings',
    addNewTherapist: 'Add New Therapist',
    therapistDetails: 'Enter the therapist\'s details. They will need to message the Angel Massage LINE account to link their account for OTP login.',
    therapistName: 'Name',
    therapistPhone: 'Phone Number',
    photoUrl: 'Photo URL',
    description: 'Description',
    specialties: 'Specialties, experience, etc.',
    selectSpecialty: 'Select a specialty',
    lineNotLinked: 'LINE not linked',
    remove: 'Remove',
    noBookings: 'No bookings yet',
    room: 'Room',
    assignedTo: 'Assigned to',
    created: 'Created',
    reportPeriod: 'Report Period',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    lastYear: 'Last Year',
    allTime: 'All Time',
    customRange: 'Custom Range',
    startDate: 'Start Date',
    endDate: 'End Date',
    selectDateBtn: 'Select date',
    filterByTherapist: 'Filter by Therapist',
    allTherapists: 'All Therapists',
    totalRevenue: 'Total Revenue',
    completedBookings: 'Completed Bookings',
    avgPerBooking: 'Avg. per Booking',
    revenueByTherapist: 'Revenue by Therapist',
    revenueByService: 'Revenue by Service Type',
    noCompletedBookings: 'No completed bookings in this period',
    customerList: 'Customer List',
    uniqueCustomers: 'unique customers',
    searchCustomer: 'Search by name, phone, LINE...',
    noCustomersFound: 'No customers found',
    selectCustomer: 'Select a Customer',
    customerHistory: 'History',
    totalSpent: 'total spent',
    contactInfo: 'Contact Information',
    filterByDateRange: 'Filter by Date Range',
    bookingHistory: 'Booking History',
    note: 'Note',
    manuals: 'Manuals',
    rates: 'Rates',
    ratesDescription: 'Edit prices below and click Save to update.',
    lineRichMenu: 'LINE Rich Menu',
    lineRichMenuDesc: 'Setup or update the LINE Rich Menu for all users. Includes buttons for booking, viewing therapists, rates, website, contact, and staff login.',
    operatingManuals: 'Operating Manuals',
    manualsDesc: 'Download PDF operating manuals for administrators and therapists in English and Thai.',
    adminManual: 'Admin Manual',
    adminManualDesc: 'Complete guide for managing bookings, therapists, revenue, and system settings.',
    adminManualTH: 'Admin Manual (Thai / ภาษาไทย)',
    therapistManual: 'Therapist Manual',
    therapistManualDesc: 'Guide for therapists on managing bookings, LINE integration, and service guidelines.',
    therapistManualTH: 'Therapist Manual (Thai / ภาษาไทย)',
    siteCredentials: 'Site Access Credentials',
    siteCredentialsDesc: 'Confidential PDF with all admin usernames and passwords, plus how therapists log in. Admin-only — not stored on any public page.',
    siteCredentialsWarning: 'This file contains login passwords. Keep it private and store it in a password manager.',
    downloadCredentials: 'Download Credentials PDF',
    previewCredentials: 'Preview',
    preview: 'Preview',
    previewHint: 'Preview the document to confirm it looks right before you download or share it.',
    sendManualLine: 'Send Manual to Therapist',
    sendManualLineDesc: 'Send the therapist manual download link directly to a therapist\'s LINE account.',
    noLinkedTherapists: 'No therapists with linked LINE accounts. Therapists need to link their LINE account first.',
    editTherapist: 'Edit Therapist',
    editTherapistDesc: 'Update the therapist\'s information.',
    uploadPhoto: 'Upload Photo',
    uploading: 'Uploading...',
    changePhoto: 'Change Photo',
    saveChanges: 'Save Changes',
    addTherapistBtn: 'Add Therapist',
    selectATherapist: 'Select a therapist',
    noAvailableTherapists: 'No available therapists. Please check therapist availability.',
    assignBookingFor: 'Select a therapist to assign to this booking for',
    lineIdLabel: 'LINE ID',
    lineIdHelp: 'The therapist\'s LINE ID for contact purposes',
    noLineId: 'No LINE ID',
    generateOTP: 'Generate OTP',
    lineLinked: 'LINE linked',
    showingBookings: 'Showing',
    completedLabel: 'completed',
    spent: 'spent',
    selectStartDate: 'Start date',
    selectEndDate: 'End date',
    clear: 'Clear',
    to: 'to',
  },
  passwordChange: {
    title: 'Change Your Password',
    description: 'Change the password for your own admin account. This is independent — it does not affect any other admin.',
    currentPassword: 'Current Password',
    currentPlaceholder: 'Enter your current password',
    newPassword: 'New Password',
    minChars: 'Minimum 8 characters',
    confirmPassword: 'Confirm New Password',
    confirmPlaceholder: 'Re-enter new password',
    submit: 'Change Password',
    passwordChanged: 'Password Changed Successfully!',
    onlyYourAccount: 'Your account now uses the new password.',
  },
  
  // Therapist portal
  therapist: {
    title: 'Therapist Portal',
    manageBookings: 'Manage your bookings',
    activeBookings: 'Active Bookings',
    history: 'History',
    noActiveBookings: 'No active bookings assigned to you',
    noCompletedBookings: 'No completed bookings yet',
    customerContact: 'Customer Contact',
    phoneOnly: 'Phone only',
    specialRequests: 'Special Requests',
    requestedTime: 'Requested time',
    acceptBooking: 'Accept Booking',
    markCompleted: 'Mark as Completed',
    pendingAcceptance: 'Pending Acceptance',
  },
  
  // Services
  services: {
    thaiMassage: 'Thai Massage',
    oilMassage: 'Oil Massage',
    aromaMassage: 'Aroma Massage',
    footMassage: 'Foot Massage',
    headMassage: 'Head & Shoulder Massage',
  },
};

// Thai translations
const th = {
  freelance: {
    title: 'Angel Freelance',
    intro: 'ร่วมงานกับ Angel Freelance กรอกข้อมูลของคุณและอัปโหลดรูปถ่ายล่าสุด 2 รูป ทีมงานจะตรวจสอบใบสมัครของคุณ และจะแสดงภายใน 24 ชั่วโมง',
    formTitle: 'ลงทะเบียนข้อมูลของคุณ',
    displayName: 'ชื่อที่จะแสดง',
    firstName: 'ชื่อจริง',
    lastName: 'นามสกุล',
    location: 'สถานที่',
    locationPlaceholder: 'เช่น สุขุมวิท กรุงเทพฯ',
    workTelephone: 'เบอร์โทรศัพท์ที่ทำงาน',
    workLineId: 'Line ID ที่ทำงาน',
    workWhatsapp: 'WhatsApp ที่ทำงาน',
    email: 'อีเมล',
    emailHint: 'ถ้ามี',
    age: 'อายุ',
    height: 'ส่วนสูง',
    heightPlaceholder: 'เช่น 168 ซม.',
    contactNote: 'กรุณาระบุ Line ID และ/หรือ เบอร์ WhatsApp อย่างน้อยหนึ่งอย่าง',
    optional: 'ไม่บังคับ',
    photo1: 'อัปโหลดรูปที่ 1',
    photo2: 'อัปโหลดรูปที่ 2',
    photoHint: 'JPG หรือ PNG ไม่เกิน 8 MB',
    changePhoto: 'เปลี่ยน',
    submit: 'ส่งใบสมัคร',
    submitting: 'กำลังส่ง…',
    successTitle: 'ขอบคุณค่ะ!',
    successMsg: 'ข้อมูลของคุณถูกส่งแล้วและอยู่ระหว่างการตรวจสอบ เราจะติดต่อกลับเร็วๆ นี้',
    submitAnother: 'ส่งใบสมัครอีกครั้ง',
    errRequired: 'กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน',
    errContact: 'กรุณาระบุ Line ID หรือเบอร์ WhatsApp',
    errPhotos: 'กรุณาอัปโหลดรูปภาพทั้งสองรูป',
    errPhotoType: 'รูปภาพต้องเป็น JPG หรือ PNG',
    errPhotoSize: 'แต่ละรูปต้องมีขนาดไม่เกิน 8 MB',
    errSubmit: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
    galleryTitle: 'ฟรีแลนซ์ของเรา',
    galleryEmpty: 'ยังไม่มีฟรีแลนซ์ที่จะแสดง กรุณากลับมาตรวจสอบอีกครั้ง',
    profilesTitle: 'โปรไฟล์ Angel Freelance',
    profilesSubtitle: 'เลือกดูฟรีแลนซ์ที่พร้อมให้บริการ แตะที่รูปเพื่อดูขนาดเต็ม',
    viewProfiles: 'ดูโปรไฟล์',
    registerCta: 'ส่งโปรไฟล์ของคุณ',
    close: 'ปิด',
    lineLabel: 'LINE',
    whatsappLabel: 'WhatsApp',
    yearsOld: 'ปี',
  },
  // Common
  common: {
    refresh: 'รีเฟรช',
    logout: 'ออกจากระบบ',
    cancel: 'ยกเลิก',
    save: 'บันทึก',
    delete: 'ลบ',
    edit: 'แก้ไข',
    add: 'เพิ่ม',
    search: 'ค้นหา',
    loading: 'กำลังโหลด...',
    submit: 'ส่ง',
    back: 'กลับ',
    next: 'ถัดไป',
    done: 'เสร็จสิ้น',
    clear: 'ล้าง',
    clearFilters: 'ล้างตัวกรอง',
    all: 'ทั้งหมด',
    none: 'ไม่มี',
    yes: 'ใช่',
    no: 'ไม่',
    available: 'ว่าง',
    unavailable: 'ไม่ว่าง',
    pending: 'รอดำเนินการ',
    assigned: 'มอบหมายแล้ว',
    accepted: 'รับแล้ว',
    completed: 'เสร็จสิ้น',
    cancelled: 'ยกเลิก',
    unassigned: 'ยังไม่มอบหมาย',
    to: 'ถึง',
    minutes: 'นาที',
    min: 'นาที',
    bookings: 'การจอง',
    booking: 'การจอง',
  },
  
  // Home page
  home: {
    heroTitle: 'Angel Massage กรุงเทพฯ | บริการนวดถึงที่ 24 ชั่วโมง',
    heroSubtitle: 'คลายเครียดตลอด 24 ชั่วโมง กับบริการนวดถึงที่อันดับหนึ่งย่านสุขุมวิท นักบำบัดมืออาชีพพร้อมให้บริการถึงบ้านหรือโรงแรมในกรุงเทพฯ จองผ่านเว็บไซต์หรือ LINE ได้เลย!',
    tagline: 'บริการนวดมืออาชีพในกรุงเทพฯ • เปิดให้บริการ 24 ชั่วโมง',
    staffPortal: 'พอร์ทัลพนักงาน',
    footer: '© 2024 Angel Massage เปิดให้บริการ 24 ชั่วโมงในเขตกรุงเทพฯ',
    footerTravel: 'บริการเดินทางฟรีในรัศมี 6 กม. จากสุขุมวิท 20 ค่าแท็กซี่จะคิดเพิ่มสำหรับระยะทางเกิน 6 กม.',
    ourTherapists: 'นักบำบัดของเรา',
    serviceRates: 'อัตราค่าบริการ',
    bookNow: 'จองเลย',
    addFriendOnLine: 'เพิ่มเพื่อนใน LINE',
    freelanceButton: 'คลิกที่นี่เพื่อไปยัง Angel Freelance',
    freelanceUnderConstruction: 'อยู่ระหว่างการก่อสร้าง',
    freelanceComingSoon: 'เรากำลังสร้างสิ่งพิเศษ โปรดกลับมาตรวจสอบอีกครั้งเร็วๆ นี้',
    backToHome: 'กลับสู่หน้าหลัก',
    lineQuestionNote: 'มีคำถาม? ส่งข้อความหาเราทาง LINE ได้เลย!',
  },

  // Customer reviews
  reviews: {
    title: 'ลูกค้าของเราพูดถึงเรา',
    subtitle: 'รีวิวจริงจากลูกค้าทั่วกรุงเทพฯ',
    outOf5: 'จาก 5',
    basedOn: 'จาก',
    reviewsWord: 'รีวิว',
    reviewWordSingular: 'รีวิว',
    verified: 'ลูกค้าที่ยืนยันแล้ว',
    withTherapist: 'กับ',
  },

  // Therapist Gallery
  gallery: {
    title: 'นักบำบัดมืออาชีพของเรา',
    subtitle: 'นักบำบัดที่มีประสบการณ์และได้รับการรับรอง พร้อมให้บริการ 24 ชั่วโมง',
    viewPhotos: 'ดูรูปภาพ',
  },
  
  // Service Rates
  rates: {
    title: 'บริการและอัตราค่าบริการ',
    subtitle: 'บริการนวดระดับพรีเมียมในราคาที่แข่งขันได้',
    travelNote: 'บริการเดินทางฟรีในรัศมี 6 กม. จากสุขุมวิท 20 สำหรับสถานที่เกิน 6 กม. จะมีค่าแท็กซี่เพิ่มเติมตามอัตราด้านล่าง',
  },
  
  // Booking form
  booking: {
    title: 'จองนวดของคุณ',
    formDescription: 'กรอกข้อมูลของคุณ แล้วเราจะยืนยันการจอง',
    personalInfo: 'ข้อมูลส่วนตัว',
    name: 'ชื่อของคุณ',
    namePlaceholder: 'กรอกชื่อของคุณ',
    contactNumber: 'เบอร์ติดต่อ',
    contactPlaceholder: '+66...',
    preferredLanguage: 'ภาษาที่ต้องการสำหรับแชท',
    location: 'สถานที่',
    hotelName: 'โรงแรม / ที่อยู่',
    hotelPlaceholder: 'กรอกชื่อโรงแรมหรือที่อยู่บ้าน',
    roomNumber: 'หมายเลขห้อง',
    roomPlaceholder: 'หมายเลขห้อง',
    contactMethod: 'ช่องทางการติดต่อที่ต้องการ',
    lineId: 'LINE ID (ไม่จำเป็น)',
    lineIdPlaceholder: '@yourlineid',
    lineIdHelp: 'กรอก LINE ID ของคุณที่ขึ้นต้นด้วย @',
    linePlaceholder: '@yourlineid',
    whatsapp: 'WhatsApp (ไม่จำเป็น)',
    whatsappPlaceholder: '+66812345678',
    whatsappHelp: 'ใส่รหัสประเทศ (เช่น +66 สำหรับประเทศไทย)',
    contactRequired: 'กรุณาระบุช่องทางติดต่ออย่างน้อยหนึ่งช่องทาง',
    serviceDetails: 'รายละเอียดบริการ',
    serviceType: 'ประเภทบริการ',
    selectService: 'เลือกบริการ',
    duration: 'ระยะเวลา',
    selectDuration: 'เลือกระยะเวลา',
    totalPrice: 'ราคารวม',
    preferredTherapist: 'นักบำบัดที่ต้องการ (ไม่จำเป็น)',
    anyTherapist: 'นักบำบัดที่ว่างอยู่',
    preferredDateTime: 'วันและเวลาที่ต้องการ (ไม่จำเป็น)',
    specialRequests: 'คำขอพิเศษ (ไม่จำเป็น)',
    specialPlaceholder: 'คำขอพิเศษหรือหมายเหตุ...',
    submitBooking: 'ส่งการจอง',
    submitting: 'กำลังส่ง...',
    bookingSuccess: 'ส่งการจองสำเร็จ!',
    bookingSuccessDesc: 'เราจะติดต่อกลับเพื่อยืนยันในเร็วๆ นี้',
    price: 'ราคา',
  },
  
  // Login page
  login: {
    staffPortal: 'พอร์ทัลพนักงาน',
    therapist: 'นักบำบัด',
    admin: 'ผู้ดูแลระบบ',
    therapistLogin: 'เข้าสู่ระบบนักบำบัด',
    adminLogin: 'เข้าสู่ระบบผู้ดูแล',
    enterOtp: 'กรอกรหัส OTP ที่ส่งไปยัง LINE ของคุณ',
    enterPhone: 'กรอกเบอร์โทรศัพท์เพื่อรับ OTP ผ่าน LINE',
    phoneNumber: 'เบอร์โทรศัพท์',
    phonePlaceholder: '0971344376',
    sendOtp: 'ส่ง OTP ผ่าน LINE',
    haveOtp: 'ฉันมี OTP จากแอดมิน',
    enterAdminOtp: 'กรอกรหัส OTP ที่ได้รับจากแอดมิน',
    or: 'หรือ',
    sendingOtp: 'กำลังส่ง OTP...',
    enterOtpCode: 'กรอกรหัส OTP',
    verifyLogin: 'ยืนยันและเข้าสู่ระบบ',
    verifying: 'กำลังยืนยัน...',
    differentPhone: 'ใช้เบอร์โทรอื่น',
    email: 'อีเมล',
    emailPlaceholder: 'admin@angelmassage.com',
    password: 'รหัสผ่าน',
    loginBtn: 'เข้าสู่ระบบ',
    loggingIn: 'กำลังเข้าสู่ระบบ...',
    backToBooking: '← กลับไปหน้าจอง',
    adminCredentials: 'กรอกข้อมูลเข้าสู่ระบบผู้ดูแล',
    enterNamePassword: 'กรอกชื่อและรหัสผ่านเพื่อเข้าสู่ระบบ',
    yourName: 'ชื่อของคุณ',
    namePlaceholder: 'เช่น Miw',
    forgotPassword: 'ลืมรหัสผ่าน? / เข้าสู่ระบบครั้งแรก',
    verifyIdentity: 'ยืนยันตัวตนด้วยชื่อและเบอร์โทรศัพท์',
    identityInfo: 'กรอกชื่อและเบอร์โทรศัพท์ที่ลงทะเบียนกับแอดมินเพื่อยืนยันตัวตน',
    verifyAndContinue: 'ยืนยันและดำเนินการต่อ',
    backToLogin: 'กลับไปหน้าเข้าสู่ระบบ',
    askAdminOtp: 'ขอให้แอดมินสร้างรหัส OTP ให้คุณจากพอร์ทัลแอดมิน (แท็บนักบำบัด)',
    createPassword: 'สร้างรหัสผ่านสำหรับเข้าสู่ระบบครั้งต่อไป',
    otpVerified: 'ยืนยัน OTP สำเร็จ! สร้างรหัสผ่านสำหรับเข้าสู่ระบบครั้งต่อไป',
    newPassword: 'รหัสผ่านใหม่',
    minChars: 'อย่างน้อย 6 ตัวอักษร',
    confirmPassword: 'ยืนยันรหัสผ่าน',
    confirmPlaceholder: 'กรอกรหัสผ่านอีกครั้ง',
    saving: 'กำลังบันทึก...',
    setPasswordLogin: 'ตั้งรหัสผ่านและเข้าสู่ระบบ',
  },
  
  // Admin dashboard
  admin: {
    title: 'แดชบอร์ดผู้ดูแล',
    welcome: 'ยินดีต้อนรับ',
    changePassword: 'เปลี่ยนรหัสผ่าน',
    dashboard: 'แดชบอร์ด',
    bookings: 'การจอง',
    calendar: 'ปฏิทิน',
    therapists: 'นักบำบัด',
    revenue: 'รายได้',
    customers: 'ลูกค้า',
    totalBookings: 'การจองทั้งหมด',
    inProgress: 'กำลังดำเนินการ',
    therapistAvailability: 'สถานะนักบำบัด',
    quickActions: 'การดำเนินการด่วน',
    viewBookings: 'ดูการจอง',
    calendarView: 'มุมมองปฏิทิน',
    addTherapist: 'เพิ่มนักบำบัด',
    assignTherapist: 'มอบหมายนักบำบัด',
    reassign: 'มอบหมายใหม่',
    markComplete: 'ทำเครื่องหมายเสร็จสิ้น',
    selectDate: 'เลือกวันที่',
    bookingsFor: 'การจองสำหรับ',
    noBookingsDate: 'ไม่มีการจองในวันนี้',
    pinkDatesHaveBookings: 'วันที่สีชมพูมีการจอง',
    addNewTherapist: 'เพิ่มนักบำบัดใหม่',
    therapistDetails: 'กรอกข้อมูลนักบำบัด พวกเขาจะต้องส่งข้อความไปยังบัญชี LINE ของ Angel Massage เพื่อเชื่อมต่อบัญชีสำหรับการเข้าสู่ระบบด้วย OTP',
    therapistName: 'ชื่อ',
    therapistPhone: 'เบอร์โทรศัพท์',
    photoUrl: 'URL รูปภาพ',
    description: 'คำอธิบาย',
    specialties: 'ความเชี่ยวชาญ ประสบการณ์ ฯลฯ',
    selectSpecialty: 'เลือกความเชี่ยวชาญ',
    lineNotLinked: 'ยังไม่เชื่อมต่อ LINE',
    remove: 'ลบ',
    noBookings: 'ยังไม่มีการจอง',
    room: 'ห้อง',
    assignedTo: 'มอบหมายให้',
    created: 'สร้างเมื่อ',
    reportPeriod: 'ช่วงเวลารายงาน',
    last7Days: '7 วันที่ผ่านมา',
    last30Days: '30 วันที่ผ่านมา',
    lastYear: 'ปีที่ผ่านมา',
    allTime: 'ทั้งหมด',
    customRange: 'กำหนดเอง',
    startDate: 'วันเริ่มต้น',
    endDate: 'วันสิ้นสุด',
    selectDateBtn: 'เลือกวันที่',
    filterByTherapist: 'กรองตามนักบำบัด',
    allTherapists: 'นักบำบัดทั้งหมด',
    totalRevenue: 'รายได้รวม',
    completedBookings: 'การจองที่เสร็จสิ้น',
    avgPerBooking: 'เฉลี่ยต่อการจอง',
    revenueByTherapist: 'รายได้ตามนักบำบัด',
    revenueByService: 'รายได้ตามประเภทบริการ',
    noCompletedBookings: 'ไม่มีการจองที่เสร็จสิ้นในช่วงนี้',
    customerList: 'รายชื่อลูกค้า',
    uniqueCustomers: 'ลูกค้าไม่ซ้ำ',
    searchCustomer: 'ค้นหาด้วยชื่อ เบอร์โทร LINE...',
    noCustomersFound: 'ไม่พบลูกค้า',
    selectCustomer: 'เลือกลูกค้า',
    customerHistory: 'ประวัติ',
    totalSpent: 'ยอดใช้จ่ายรวม',
    contactInfo: 'ข้อมูลติดต่อ',
    filterByDateRange: 'กรองตามช่วงวันที่',
    bookingHistory: 'ประวัติการจอง',
    note: 'หมายเหตุ',
    manuals: 'คู่มือ',
    rates: 'อัตราค่าบริการ',
    ratesDescription: 'แก้ไขราคาด้านล่างแล้วกดบันทึกเพื่ออัปเดต',
    lineRichMenu: 'เมนูริชไลน์',
    lineRichMenuDesc: 'ตั้งค่าหรืออัปเดตเมนูริชไลน์สำหรับผู้ใช้ทุกคน รวมปุ่มจอง ดูนักบำบัด ราคา เว็บไซต์ ติดต่อ และเข้าสู่ระบบพนักงาน',
    operatingManuals: 'คู่มือการใช้งาน',
    manualsDesc: 'ดาวน์โหลดคู่มือการใช้งาน PDF สำหรับผู้ดูแลระบบและนักบำบัดในภาษาอังกฤษและไทย',
    adminManual: 'คู่มือผู้ดูแลระบบ',
    adminManualDesc: 'คู่มือฉบับสมบูรณ์สำหรับการจัดการการจอง นักบำบัด รายได้ และการตั้งค่าระบบ',
    adminManualTH: 'คู่มือผู้ดูแลระบบ (ภาษาไทย)',
    therapistManual: 'คู่มือนักบำบัด',
    therapistManualDesc: 'คู่มือสำหรับนักบำบัดเกี่ยวกับการจัดการการจอง การเชื่อมต่อ LINE และแนวทางการให้บริการ',
    therapistManualTH: 'คู่มือนักบำบัด (ภาษาไทย)',
    siteCredentials: 'ข้อมูลการเข้าสู่ระบบเว็บไซต์',
    siteCredentialsDesc: 'ไฟล์ PDF ลับที่มีชื่อผู้ใช้และรหัสผ่านของผู้ดูแลทั้งหมด รวมถึงวิธีที่นักบำบัดเข้าสู่ระบบ สำหรับผู้ดูแลเท่านั้น ไม่ได้จัดเก็บบนหน้าสาธารณะ',
    siteCredentialsWarning: 'ไฟล์นี้มีรหัสผ่านสำหรับเข้าสู่ระบบ โปรดเก็บเป็นความลับและบันทึกไว้ในโปรแกรมจัดการรหัสผ่าน',
    downloadCredentials: 'ดาวน์โหลด PDF ข้อมูลเข้าสู่ระบบ',
    previewCredentials: 'ดูตัวอย่าง',
    preview: 'ดูตัวอย่าง',
    previewHint: 'ดูตัวอย่างเอกสารเพื่อตรวจสอบความถูกต้องก่อนดาวน์โหลดหรือแชร์',
    sendManualLine: 'ส่งคู่มือให้นักบำบัด',
    sendManualLineDesc: 'ส่งลิงก์ดาวน์โหลดคู่มือนักบำบัดไปยังบัญชี LINE ของนักบำบัดโดยตรง',
    noLinkedTherapists: 'ไม่มีนักบำบัดที่เชื่อมต่อบัญชี LINE นักบำบัดต้องเชื่อมต่อบัญชี LINE ก่อน',
    editTherapist: 'แก้ไขนักบำบัด',
    editTherapistDesc: 'อัปเดตข้อมูลนักบำบัด',
    uploadPhoto: 'อัปโหลดรูปภาพ',
    uploading: 'กำลังอัปโหลด...',
    changePhoto: 'เปลี่ยนรูปภาพ',
    saveChanges: 'บันทึกการเปลี่ยนแปลง',
    addTherapistBtn: 'เพิ่มนักบำบัด',
    selectATherapist: 'เลือกนักบำบัด',
    noAvailableTherapists: 'ไม่มีนักบำบัดว่าง กรุณาตรวจสอบสถานะนักบำบัด',
    assignBookingFor: 'เลือกนักบำบัดสำหรับการจองของ',
    lineIdLabel: 'LINE ID',
    lineIdHelp: 'LINE ID ของนักบำบัดสำหรับการติดต่อ',
    noLineId: 'ไม่มี LINE ID',
    generateOTP: 'สร้าง OTP',
    lineLinked: 'เชื่อมต่อ LINE แล้ว',
    showingBookings: 'แสดง',
    completedLabel: 'เสร็จสิ้น',
    spent: 'ใช้จ่าย',
    selectStartDate: 'วันเริ่มต้น',
    selectEndDate: 'วันสิ้นสุด',
    clear: 'ล้าง',
    to: 'ถึง',
  },
  passwordChange: {
    title: 'เปลี่ยนรหัสผ่านของคุณ',
    description: 'เปลี่ยนรหัสผ่านสำหรับบัญชีผู้ดูแลของคุณเอง เป็นอิสระและไม่มีผลต่อผู้ดูแลคนอื่น',
    currentPassword: 'รหัสผ่านปัจจุบัน',
    currentPlaceholder: 'กรอกรหัสผ่านปัจจุบันของคุณ',
    newPassword: 'รหัสผ่านใหม่',
    minChars: 'อย่างน้อย 8 ตัวอักษร',
    confirmPassword: 'ยืนยันรหัสผ่านใหม่',
    confirmPlaceholder: 'กรอกรหัสผ่านใหม่อีกครั้ง',
    submit: 'เปลี่ยนรหัสผ่าน',
    passwordChanged: 'เปลี่ยนรหัสผ่านสำเร็จ!',
    onlyYourAccount: 'บัญชีของคุณใช้รหัสผ่านใหม่แล้ว',
  },
  
  // Therapist portal
  therapist: {
    title: 'พอร์ทัลนักบำบัด',
    manageBookings: 'จัดการการจองของคุณ',
    activeBookings: 'การจองที่ใช้งานอยู่',
    history: 'ประวัติ',
    noActiveBookings: 'ไม่มีการจองที่มอบหมายให้คุณ',
    noCompletedBookings: 'ยังไม่มีการจองที่เสร็จสิ้น',
    customerContact: 'ติดต่อลูกค้า',
    phoneOnly: 'โทรศัพท์เท่านั้น',
    specialRequests: 'คำขอพิเศษ',
    requestedTime: 'เวลาที่ต้องการ',
    acceptBooking: 'รับการจอง',
    markCompleted: 'ทำเครื่องหมายว่าเสร็จสิ้น',
    pendingAcceptance: 'รอการรับ',
  },
  
  // Services
  services: {
    thaiMassage: 'นวดแผนไทย',
    oilMassage: 'นวดน้ำมัน',
    aromaMassage: 'นวดอโรมา',
    footMassage: 'นวดเท้า',
    headMassage: 'นวดศีรษะและไหล่',
  },
};

const translations: Record<string, Record<string, any>> = { en, th };

// Canonical therapist specialty phrases — the ONLY values selectable in the admin
// portal. Each MUST have a matching (normalized) entry in DESCRIPTION_TRANSLATIONS_TH
// so the public gallery can render it in Thai. This prevents odd, untranslatable
// free-text phrases from being saved.
export const SPECIALTY_PHRASES: string[] = [
  'Experienced massage therapist',
  'Professional massage specialist',
  'Expert in traditional Thai massage',
  'Skilled in oil massage techniques',
  'Experienced Thai Massage',
  'Aromatherapy massage',
];

// Therapist specialty descriptions: content-based EN -> TH translation.
// Keyed by normalized (trimmed, lowercased) English text so it works for ANY
// therapist regardless of id. Therapists added via admin/upsert get UUID ids and
// have no per-id translation key, so id-based lookups silently fell back to English.
const DESCRIPTION_TRANSLATIONS_TH: Record<string, string> = {
  'experienced massage therapist': 'นักนวดที่มีประสบการณ์',
  'professional massage specialist': 'ผู้เชี่ยวชาญด้านการนวดมืออาชีพ',
  'expert in traditional thai massage': 'ผู้เชี่ยวชาญด้านนวดแผนไทย',
  'skilled in oil massage techniques': 'เชี่ยวชาญเทคนิคการนวดน้ำมัน',
  'experienced thai massage': 'นวดแผนไทยที่มีประสบการณ์',
  'aromatherapy massage': 'นวดอโรมาเธอราพี',
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'th' : 'en');
  };

  const translateDescription = (text: string): string => {
    if (!text) return '';
    if (language !== 'th') return text;
    const normalized = text.trim().toLowerCase();
    return DESCRIPTION_TRANSLATIONS_TH[normalized] || text;
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    translateDescription,
    isEnglish: language === 'en',
    isThai: language === 'th',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
